import {
  isValidReferenceCode,
  type Order,
  type OrderItem,
  type OrderTotals,
} from '@planner/shared';
import { brands } from '@planner/config';
import {
  doorsOrderFingerprint,
  matchStoredDoorLines,
  parseDoorsDesign,
  type DoorsDesignV1,
  type HistoricalSizeConfirmation,
} from '@planner/shared/doors';
import { apiBaseUrl, region } from './env.js';
import { regionFetch } from './region-availability.js';

export class CartApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
  }
}

export interface SaveDoorCart {
  region: string;
  brand: string;
  design: DoorsDesignV1;
  items: OrderItem[];
  totals: OrderTotals;
  baseReference?: string;
  editingLineId?: string;
  removingLineId?: string;
  historicalSizeConfirmation?: HistoricalSizeConfirmation;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isOrderItem(value: unknown): value is OrderItem {
  return (
    isRecord(value) &&
    typeof value.sku === 'string' &&
    typeof value.description === 'string' &&
    Number.isSafeInteger(value.quantity) &&
    typeof value.unitPrice === 'number' &&
    Number.isFinite(value.unitPrice) &&
    (value.options === undefined ||
      (isRecord(value.options) &&
        Object.values(value.options).every((item) => typeof item === 'string')))
  );
}

function hasOptionalStrings(value: Record<string, unknown>, fields: readonly string[]): boolean {
  return fields.every((key) => value[key] === undefined || typeof value[key] === 'string');
}

function hasFiniteNumbers(value: Record<string, unknown>, fields: readonly string[]): boolean {
  return fields.every((key) => typeof value[key] === 'number' && Number.isFinite(value[key]));
}

function orderUrl(reference: string, suffix = ''): string {
  return `${apiBaseUrl}/orders/${encodeURIComponent(reference)}${suffix}?region=${encodeURIComponent(region.code)}`;
}

function isOrder(value: unknown): value is Order {
  if (
    !isRecord(value) ||
    typeof value.referenceCode !== 'string' ||
    !isValidReferenceCode(value.referenceCode) ||
    value.region !== region.code ||
    typeof value.brand !== 'string' ||
    !Object.hasOwn(brands, value.brand) ||
    (value.status !== 'draft' && value.status !== 'submitted' && value.status !== 'emailed') ||
    !Array.isArray(value.items) ||
    !value.items.every(isOrderItem) ||
    !isRecord(value.customer) ||
    !hasOptionalStrings(value.customer, ['name', 'email', 'phone', 'postcode']) ||
    !isRecord(value.totals) ||
    !hasFiniteNumbers(value.totals, ['subtotal', 'tax', 'total']) ||
    typeof value.totals.currency !== 'string' ||
    !isRecord(value.design) ||
    !isRecord(value.channel) ||
    (value.channel.type !== 'direct' &&
      !(
        value.channel.type === 'bunnings' &&
        (value.channel.storeId === undefined || typeof value.channel.storeId === 'string') &&
        (value.channel.storeName === undefined || typeof value.channel.storeName === 'string')
      )) ||
    (value.doorLineIds !== undefined &&
      (!Array.isArray(value.doorLineIds) ||
        !value.doorLineIds.every((id: unknown) => typeof id === 'string'))) ||
    typeof value.createdAt !== 'string' ||
    typeof value.updatedAt !== 'string' ||
    (value.appVersion !== undefined && typeof value.appVersion !== 'string')
  ) {
    return false;
  }
  return parseDoorsDesign(value.design) !== undefined;
}

async function readCartResponse(response: Response): Promise<Order> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new CartApiError('The saved Cart response was invalid.', 'invalid_response');
  }
  if (!response.ok) {
    const message =
      isRecord(body) && typeof body.error === 'string' ? body.error : 'The Cart request failed.';
    const code = isRecord(body) && typeof body.code === 'string' ? body.code : 'request_failed';
    throw new CartApiError(message, code);
  }
  if (!isRecord(body) || !isOrder(body.order)) {
    throw new CartApiError('The saved Cart response was invalid.', 'invalid_response');
  }
  return body.order;
}

export async function fetchCart(reference: string): Promise<Order> {
  const response = await regionFetch(orderUrl(reference));
  const order = await readCartResponse(response);
  if (order.referenceCode !== reference) {
    throw new CartApiError('The saved Cart reference did not match.', 'invalid_response');
  }
  return order;
}

function matchesCandidate(order: Order, input: SaveDoorCart): boolean {
  const design = parseDoorsDesign(order.design);
  return (
    order.status === 'draft' &&
    order.region === input.region &&
    order.brand === input.brand &&
    !!design?.doors.length &&
    !!matchStoredDoorLines(design, order.items, order.doorLineIds) &&
    doorsOrderFingerprint(order.design, order.items) ===
      doorsOrderFingerprint(input.design, input.items) &&
    order.totals.subtotal === input.totals.subtotal &&
    order.totals.tax === input.totals.tax &&
    order.totals.total === input.totals.total &&
    order.totals.currency === input.totals.currency
  );
}

export async function saveDoorCart(input: SaveDoorCart): Promise<Order> {
  let response: Response;
  try {
    response = await regionFetch(`${apiBaseUrl}/orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  } catch {
    throw new CartApiError(
      'The save could not be confirmed. Your draft and previous Cart have been kept.',
      'uncertain',
    );
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new CartApiError(
      'The save response could not be confirmed. Your draft and previous Cart have been kept.',
      'uncertain',
    );
  }
  if (!response.ok && response.status < 500) {
    const message =
      isRecord(body) && typeof body.error === 'string' ? body.error : 'The Cart request failed.';
    const code = isRecord(body) && typeof body.code === 'string' ? body.code : 'request_failed';
    throw new CartApiError(message, code);
  }
  const reference =
    isRecord(body) &&
    typeof body.referenceCode === 'string' &&
    isValidReferenceCode(body.referenceCode)
      ? body.referenceCode
      : undefined;
  if (
    response.ok &&
    [200, 201].includes(response.status) &&
    reference &&
    isRecord(body) &&
    isOrder(body.order) &&
    body.order.referenceCode === reference &&
    matchesCandidate(body.order, input)
  ) {
    return body.order;
  }
  if (reference) {
    try {
      const saved = await fetchCart(reference);
      if (matchesCandidate(saved, input)) return saved;
    } catch {
      // The write may have succeeded even when its confirmation cannot be read.
    }
  }
  const detail =
    !response.ok && isRecord(body) && typeof body.error === 'string' ? ` ${body.error}` : '';
  throw new CartApiError(
    `The save could not be confirmed.${detail} Your draft and previous Cart have been kept.`,
    'uncertain',
  );
}

async function outputResponse(response: Response): Promise<unknown> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new CartApiError(
      'The server response could not be confirmed. Please retry.',
      'invalid_response',
    );
  }
  if (!response.ok) {
    const message =
      isRecord(body) && typeof body.error === 'string'
        ? body.error
        : 'The Cart request failed. Please retry.';
    throw new CartApiError(
      message,
      isRecord(body) && body.uncertain === true
        ? 'uncertain'
        : isRecord(body) && body.uncertain === false
          ? 'definite_failure'
          : 'request_failed',
    );
  }
  return body;
}

export async function confirmCartDeclarations(reference: string): Promise<string> {
  const response = await regionFetch(orderUrl(reference, '/declarations'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ drawing: true, terms: true, privacy: true }),
  });
  const body = await outputResponse(response);
  if (
    !isRecord(body) ||
    body.referenceCode !== reference ||
    typeof body.confirmationId !== 'string' ||
    !/^[A-Za-z0-9-]{1,64}$/.test(body.confirmationId)
  ) {
    throw new CartApiError(
      'The Cart declarations could not be confirmed. Please retry.',
      'invalid_response',
    );
  }
  return body.confirmationId;
}

export async function downloadCartDocument(
  reference: string,
  confirmationId: string,
  lineId?: string,
): Promise<Blob> {
  const path = lineId ? `drawings/${encodeURIComponent(lineId)}` : 'summary.pdf';
  const response = await regionFetch(
    `${orderUrl(reference, `/${path}`)}&confirmationId=${encodeURIComponent(confirmationId)}`,
  );
  if (!response.ok) await outputResponse(response);
  if (!response.headers.get('content-type')?.toLowerCase().startsWith('application/pdf')) {
    throw new CartApiError('The PDF response was invalid. Please retry.', 'invalid_response');
  }
  return response.blob();
}

export async function emailCart(
  reference: string,
  confirmationId: string,
  email: string,
): Promise<void> {
  let response: Response;
  try {
    response = await regionFetch(orderUrl(reference, '/email'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, confirmationId }),
    });
  } catch {
    throw new CartApiError(
      'The email may have been sent. Check before deliberately retrying.',
      'uncertain',
    );
  }
  let body: unknown;
  try {
    body = await outputResponse(response);
  } catch (error) {
    if (
      error instanceof CartApiError &&
      (error.code === 'invalid_response' ||
        (response.status >= 500 && error.code === 'request_failed'))
    ) {
      throw new CartApiError(
        'The email may have been sent. Check before deliberately retrying.',
        'uncertain',
      );
    }
    throw error;
  }
  if (!isRecord(body) || body.sent !== true) {
    throw new CartApiError(
      'The email may have been sent. Check before deliberately retrying.',
      'uncertain',
    );
  }
}
